// components/AdminVersionFileUploadButton.tsx
import type React from 'react'
import { useState, useRef } from 'react'

import { Button } from 'ui/Button/Button'
import { useToast } from 'shadcdn/ui/use-toast'
import { useAuthStore } from 'features/auth/useAuthStore'
import { CONFIG } from 'config'

interface AdminVersionFileUploadButtonProps {
  folder: string | number
  onUploadComplete?: (url: string) => void
}

export const AdminVersionFileUploadButton: React.FC<AdminVersionFileUploadButtonProps> = (props) => {
  const { onUploadComplete, folder } = props
  const { authToken } = useAuthStore()
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setIsUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', String(folder))

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/software-admin/upload-file/`, {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${authToken}` },
      })

      if (!response.ok) {
        throw new Error('Upload failed')
      }
      const data = await response.json()
      onUploadComplete?.(data.url)
      toast({ title: 'Upload complete', description: `File ${file.name} Uploaded` })
    } catch (error) {
      console.error('Upload error:', error)
      toast({
        title: 'Upload Failed',
        description: 'There was an error uploading the file.',
        className: 'bg-red-50',
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
      <Button
        type="button"
        disabled={isUploading}
        spinner={isUploading}
        onClick={() => fileInputRef.current?.click()}
        size="sm"
        color="light"
        className="py-0 h-6 -mt-2"
      >
        {isUploading ? 'Uploading...' : 'Upload'}
      </Button>
    </>
  )
}
